// Providers
import { AuthProvider } from '@/providers/AuthProvider';
import { SystemProvider } from '@/providers/SystemProvider';
// Routes
import { EnrollmentRoute } from '@/components/Routes/EnrollmentRoute';
import { ProtectedRoute } from '@/components/Routes/ProtectedRoute';
import { StrictlyPublicRoute } from '@/components/Routes/StrictlyPublicRoute';

import './_app.css';
import { Experimental_CssVarsProvider as CssVarsProvider } from '@mui/material/styles';

//Support Tools & Admin Portal
import { SupportLayout } from '@/components/SupportLayout';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer as ToastContainerBase } from 'react-toastify';
import { HQRoute } from '../components/Routes/HQRoute';

import Head from 'next/head';
import brandTheme from '@/theme/brandTheme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FeatureFlagProvider } from '@/providers/FeatureFlagsProvider';
import styled from 'styled-components';

const materialTheme = brandTheme;

const ToastContainer = styled(ToastContainerBase)`
  .Toastify__toast {
    padding: 0;
    min-height: auto;
    min-height: 32px;

    @media (max-width: 767px) {
      margin: 16px;
      border-radius: 4px;
    }
  }
  .Toastify__toast-body {
    padding: 0;
    color: white;
  }
`;

// React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function Protector({ Component, pageProps }) {
  // nested layouts
  const getLayout = Component.getLayout || ((page) => page);

  const { enrollmentRoute, protectedRoute, hqRoute, strictlyPublicRoute, permission } =
    Component;
  //HQ
  //------------------------------------------------------------

  if (hqRoute) {
    return (
      <HQRoute>
        <SupportLayout permission={permission}>
          {getLayout(<Component {...pageProps} />)}
        </SupportLayout>
      </HQRoute>
    );
  } else if (enrollmentRoute) {
    return (
      <EnrollmentRoute>
        <SupportLayout permission={permission}>
          {getLayout(<Component {...pageProps} />)}
        </SupportLayout>
      </EnrollmentRoute>
    );
  } else if (protectedRoute) {
    return (
      <ProtectedRoute>
        <SupportLayout permission={permission}>
          {getLayout(<Component {...pageProps} />)}
        </SupportLayout>
      </ProtectedRoute>
    );
  } else if (strictlyPublicRoute) {
    return (
      <StrictlyPublicRoute>
        <SupportLayout permission={permission}>
          {getLayout(<Component {...pageProps} />)}
        </SupportLayout>
      </StrictlyPublicRoute>
    );
  } else {
    return (
      <SupportLayout permission={permission}>
        {getLayout(<Component {...pageProps} />)}
      </SupportLayout>
    );
  }
}

function App({ Component, pageProps }) {
  return (
    <>
      <Head>
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="manifest" href="/manifest.json" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/icon?family=Material+Icons"
        />
      </Head>
      <QueryClientProvider client={queryClient}>
        {/* <ReactQueryDevtools /> */}
        <CssVarsProvider theme={materialTheme}>
          <ToastContainer
            autoClose={3000}
            hideProgressBar
            pauseOnHover={false}
            position="bottom-center"
            closeButton={false}
            icon={false}
          />
          <FeatureFlagProvider>
            <AuthProvider>
              <SystemProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Protector Component={Component} pageProps={pageProps} />
                </LocalizationProvider>
              </SystemProvider>
            </AuthProvider>
          </FeatureFlagProvider>
        </CssVarsProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
